// @refresh reset
/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useEffect, useState } from "react";
import type { User as FirebaseUser } from "firebase/auth";
import { onAuthStateChanged } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { auth, db } from "../firebase/config";
import { createLogger } from "../lib/logger";
import { setSentryUser } from "../instrumentation/sentry";
import { maybeSendWelcomeEmail } from "../lib/welcomeEmail";

const log = createLogger("AuthContext");

// ─── Context shape ──────────────────────────────────────────────────────────

export interface AuthValue {
  user: FirebaseUser | null;
  loading: boolean;
  /** Whether the user has completed the FTU questionnaire. null = still loading. */
  ftuCompleted: boolean | null;
  /** Call this after a successful FTU submission so the context stays in sync without a re-fetch. */
  setFtuCompleted: (v: boolean) => void;
}

const AuthCtx = createContext<AuthValue>({
  user: null,
  loading: true,
  ftuCompleted: null,
  setFtuCompleted: () => {},
});

// ─── Provider ───────────────────────────────────────────────────────────────

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [ftuCompleted, setFtuCompleted] = useState<boolean | null>(null);

  useEffect(() => {
    log.debug("Subscribing to onAuthStateChanged");

    let callId = 0;
    let unsubscribeUserDoc: (() => void) | null = null;

    const unsubscribe = onAuthStateChanged(auth, async (u) => {
      const myId = ++callId;

      // Clean up previous user doc listener whenever auth state changes
      if (unsubscribeUserDoc) {
        unsubscribeUserDoc();
        unsubscribeUserDoc = null;
      }

      try {
        if (u) {
          // Force-refresh the ID token BEFORE opening any Firestore listener.
          // Without this await completing first, the Firestore SDK may open the
          // snapshot with a stale token and get permission denied — even though
          // the rules allow the read. The await ensures the fresh token is
          // propagated to the Firestore SDK before the first snapshot request.
          await u.getIdToken(/* forceRefresh */ true);

          // Guard: if a newer auth event arrived while we were awaiting the
          // token refresh, bail out — that event will open its own snapshot.
          if (myId !== callId) return;

          log.info("Auth state: signed in", { uid: u.uid, emailVerified: u.emailVerified });
          setSentryUser(u.uid);

          if (u.emailVerified) {
            maybeSendWelcomeEmail(u.uid).catch((err) =>
              log.error("maybeSendWelcomeEmail failed", {
                uid: u.uid,
                error: err instanceof Error ? err.message : String(err),
              })
            );

            // Token is fresh and verified — safe to open the Firestore snapshot.
            // The user doc is guaranteed to exist at this point:
            // - sign-up: doc is written before the verification email is sent
            // - sign-in: ensureUserDocOnLogin runs before this point
            setFtuCompleted(null); // null = loading while listener connects

            let retriedFtuDoc = false;

            const subscribeUserDoc = () => {
              unsubscribeUserDoc = onSnapshot(
                doc(db, "users", u.uid),
                (snap) => {
                  if (myId !== callId) return;

                  // Account deletion in progress — the doc can legitimately
                  // vanish right before sign-out completes. Don't let that
                  // flip ftuCompleted to false and flash the onboarding
                  // overlay during teardown.
                  if (sessionStorage.getItem(`bodyown_account_deleted_${u.uid}`)) return;

                  const ftu = snap.exists()
                    ? Boolean((snap.data() as Record<string, unknown>).ftuCompleted)
                    : false;
                  setFtuCompleted(ftu);
                },
                (err) => {
                  if (myId !== callId) return;
                  if (sessionStorage.getItem(`bodyown_account_deleted_${u.uid}`)) return;

                  // Rules may briefly reject the read while the refreshed
                  // token is still propagating to the Firestore SDK. Retry
                  // once instead of permanently flashing the onboarding
                  // overlay.
                  if (err.code === "permission-denied" && !retriedFtuDoc) {
                    retriedFtuDoc = true;
                    setTimeout(() => {
                      unsubscribeUserDoc?.();
                      subscribeUserDoc();
                    }, 1500);
                    return;
                  }

                  log.warn("User doc snapshot permission denied", { error: err.message });
                  setFtuCompleted(false);
                }
              );
            };

            subscribeUserDoc();
          } else {
            // Unverified user — skip snapshot entirely.
            // ProtectedRoute sends them to /verify-email so ftuCompleted is irrelevant.
            // Opening a snapshot here risks permission denied because the user doc
            // may not exist yet (race between signUp writing the doc and
            // onAuthStateChanged firing immediately after account creation).
            setFtuCompleted(false);
          }
        } else {
          log.info("Auth state: signed out");
          setSentryUser(null);
          setFtuCompleted(null);
        }

        if (myId !== callId) return;
        setUser(u);
        setLoading(false);
      } catch (err) {
        log.error("onAuthStateChanged callback failed", {
          error: err instanceof Error ? err.message : String(err),
        });
        if (myId !== callId) return;
        setUser(u);
        setLoading(false);
      }
    });

    return () => {
      log.debug("Unsubscribing from onAuthStateChanged");
      callId++;
      unsubscribe();
      if (unsubscribeUserDoc) unsubscribeUserDoc();
    };
  }, []);

  return (
    <AuthCtx.Provider value={{ user, loading, ftuCompleted, setFtuCompleted }}>
      {children}
    </AuthCtx.Provider>
  );
};

// ─── Hook ───────────────────────────────────────────────────────────────────

export const useAuth = () => useContext(AuthCtx);

export default AuthProvider;